import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Eye, Lightbulb, MapPin, Ruler, UserRound } from "lucide-react";
import { Layout } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { MapPicker } from "@/components/MapPicker";
import api, { formatApiError } from "@/lib/api";
import { inr } from "@/lib/format";
import { BookingCalendar } from "./components/BookingCalendar";

function Spec({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">
        {Icon && <Icon size={11} />} {label}
      </div>
      <div className="mt-1 font-mono text-sm font-semibold text-slate-900">{value || "-"}</div>
    </div>
  );
}

export function PartnerHoardingDetail() {
  const { listingId } = useParams();
  const nav = useNavigate();
  const [listing, setListing] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      api.get(`/partner-network/listings/${listingId}`, { skipGlobalLoader: true }),
      api.get(`/partner-network/listings/${listingId}/schedule`, { skipGlobalLoader: true }),
    ])
      .then(([listingRes, scheduleRes]) => {
        setListing(listingRes.data);
        setSchedule(Array.isArray(scheduleRes.data) ? scheduleRes.data : []);
        setError("");
      })
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Partner inventory could not be loaded."));
  }, [listingId]);

  if (error) {
    return (
      <Layout title="Partner Inventory" actions={<button onClick={() => nav(-1)} className="hx-secondary-action"><ArrowLeft size={14} /> Back</button>}>
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 text-sm font-semibold text-amber-900">{error}</div>
      </Layout>
    );
  }

  if (!listing) {
    return (
      <Layout title="Partner Inventory">
        <div className="space-y-5 animate-pulse">
          <div className="h-72 rounded-lg bg-slate-200" />
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            <div className="h-48 rounded-lg bg-slate-200 lg:col-span-2" />
            <div className="h-48 rounded-lg bg-slate-200" />
          </div>
        </div>
      </Layout>
    );
  }

  const photos = listing.photos?.length ? listing.photos : (listing.images || []).map((url) => ({ url }));
  const heroPhoto = photos[0]?.url;
  const hasMap = listing.latitude && listing.longitude;
  const scheduleStats = schedule.reduce((acc, entry) => {
    if (String(entry.type || "").toLowerCase() === "booking") acc.bookings += 1;
    if (String(entry.type || "").toLowerCase() === "maintenance") acc.maintenance += 1;
    return acc;
  }, { bookings: 0, maintenance: 0 });

  return (
    <Layout
      title={listing.name}
      subtitle={`${listing.hoarding_code} · ${listing.area}, ${listing.city}`}
      actions={<button onClick={() => nav(-1)} className="hx-secondary-action"><ArrowLeft size={14} /> Back</button>}
    >
      <div className="space-y-5">
        <section className="hx-card overflow-hidden border border-slate-200/80">
          <div className={heroPhoto ? "grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px]" : "grid grid-cols-1"}>
            <div className="p-6">
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge status="Available" />
                <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700 ring-1 ring-blue-100">Partner n/w</span>
              </div>
              <div className="mt-5">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500">{listing.hoarding_code}</div>
                <h2 className="mt-1 font-display text-3xl font-semibold text-slate-950">{listing.name}</h2>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm font-medium text-slate-600">
                  <span className="inline-flex items-center gap-1.5"><MapPin size={15} />{listing.area}, {listing.city}</span>
                  <span className="inline-flex items-center gap-1.5"><Ruler size={15} />{listing.width}ft x {listing.height}ft</span>
                  <span className="inline-flex items-center gap-1.5"><UserRound size={15} />{listing.owner_name || "Partner owner"}</span>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Spec label="Display Min" value={inr(listing.min_display_charge || 0)} />
                <Spec label="Printing Min" value={inr(listing.min_printing_charge || 0)} />
                <Spec label="Mounting Min" value={inr(listing.min_mounting_charge || 0)} />
                <Spec label="Commission" value={`${listing.effective_commission_pct || 0}%`} />
              </div>
            </div>
            {heroPhoto && (
              <div className="relative min-h-[220px] bg-slate-100">
                <img src={heroPhoto} alt="" className="absolute inset-0 h-full w-full object-cover" />
              </div>
            )}
          </div>
        </section>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-5">
            <section className="hx-card border border-slate-200/80 p-6">
              <div className="mb-5 grid grid-cols-2 gap-3">
                <Spec label="Booked Windows" value={scheduleStats.bookings} />
                <Spec label="Maintenance" value={scheduleStats.maintenance} />
              </div>
              <BookingCalendar
                schedule={schedule}
                onDayClick={() => {}}
              />
            </section>

            <section className="hx-card border border-slate-200/80 p-6">
              <h3 className="font-display font-semibold text-slate-950">Specifications</h3>
              <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                <Spec icon={Ruler} label="Size" value={`${listing.width}ft x ${listing.height}ft`} />
                <Spec icon={Eye} label="Sqft" value={listing.sqft || (listing.width * listing.height)} />
                <Spec icon={MapPin} label="Facing" value={listing.facing || "-"} />
                <Spec icon={Lightbulb} label="Lighting" value={listing.lighting ? "Yes" : "No"} />
                <Spec label="Type" value={listing.type} />
                <Spec label="Printing By" value={listing.printing_responsibility} />
                <Spec label="Mounting By" value={listing.mounting_responsibility} />
                <Spec label="Status" value={listing.partner_visibility_status} />
              </div>
              {listing.notes && <div className="mt-4 rounded-lg border border-slate-100 bg-slate-50 p-4 text-sm text-slate-600">{listing.notes}</div>}
            </section>
          </div>

          <aside className="space-y-5">
            <section className="hx-card border border-slate-200/80 p-6">
              <h3 className="font-display font-semibold text-slate-950">Partner Owner</h3>
              <p className="mt-2 text-sm font-semibold text-slate-800">{listing.owner_name || "-"}</p>
              {listing.owner_notes && <p className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-600">{listing.owner_notes}</p>}
            </section>
            <section className="hx-card border border-slate-200/80 p-6">
              <h3 className="font-display font-semibold text-slate-950">Address</h3>
              <p className="mt-2 text-sm text-slate-600">{listing.address || "-"}</p>
              {listing.landmark && <p className="mt-2 text-xs font-semibold text-slate-500">Landmark: {listing.landmark}</p>}
            </section>
            {hasMap && (
              <section className="hx-card border border-slate-200/80 p-6">
                <h3 className="font-display font-semibold text-slate-950 mb-3">Location</h3>
                <MapPicker value={{ lat: listing.latitude, lng: listing.longitude }} onChange={() => {}} height={260} />
              </section>
            )}
          </aside>
        </div>

        {photos.length > 0 && (
          <section className="hx-card border border-slate-200/80 p-6">
            <h3 className="font-display font-semibold text-slate-950 mb-4">Photos</h3>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
              {photos.map((p, i) => (
                <div key={`${p.url}-${i}`} className="aspect-[4/3] overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                  <img src={p.url} alt="" className="h-full w-full object-cover" />
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
}
